import { useEffect, useState } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { withBrand } from '../utils/seoTitle';

interface SEOHeadProps {
  pageId: string;
  noindex?: boolean;
  // Used when the page has no page_copy row; a row added in admin still wins.
  defaultTitle?: string;
  defaultDescription?: string;
}

interface PageCopy {
  title: string;
  subtitle: string;
  seoTitle?: string;
  seoDescription?: string;
}

interface DefaultSeoSettings {
  defaultSeoTitle: string;
  defaultSeoDescription: string;
}

const PAGE_ID_TO_PATH: Record<string, string> = {
  'home': '/',
  'background_remover': '/remove-background',
  'blog_writer': '/blog-writer',
  'product_description': '/product-description',
  'product_collage': '/product-collage',
  'social_media': '/social-media',
  'email_copy': '/email-copy',
  'banner_creator': '/banner-creator',
  'photo_retoucher': '/photo-retoucher',
  'ai_banner_generator': '/banner-generator',
  'gift_guide_creator': '/gift-guide',
  'image_resizer': '/image-resizer',
  'image_generator': '/lifestyle-image-generator',
  'seo_content_audit': '/seo-audit',
  'ad_copy_generator': '/ad-copy',
  'product_listing_agent': '/product-listing-agent',
  'reddit_scraper': '/reddit-scraper',
  'photo_studio': '/photo-studio',
  'pricing': '/pricing',
  'contact': '/contact',
  'blog': '/blog',
  'bulk_product_description_writer': '/bulk-product-description-writer',
  'automate_product_photos_dropshipping': '/automate-product-photos-for-dropshipping',
  'ai_product_photos_jewelry': '/ai-product-photos-for-jewelry',
  'lifestyle_image_generator_ecommerce': '/lifestyle-image-generator-for-ecommerce',
  'ai_product_photos_pod': '/ai-product-photos-for-print-on-demand',
  'dfy_pricing': '/done-for-you-content',
  'demos': '/demos',
};

const TOOL_APP_CATEGORY: Record<string, string> = {
  'background_remover': 'DesignApplication',
  'blog_writer': 'BusinessApplication',
  'product_description': 'BusinessApplication',
  'social_media': 'BusinessApplication',
  'image_resizer': 'DesignApplication',
  'image_generator': 'DesignApplication',
  'ad_copy_generator': 'BusinessApplication',
  'product_listing_agent': 'BusinessApplication',
  'photo_studio': 'DesignApplication',
};

const PAGE_ID_TO_LABEL: Record<string, string> = {
  'home': 'Home',
  'background_remover': 'Background Remover',
  'blog_writer': 'Blog Writer',
  'product_description': 'Product Descriptions',
  'social_media': 'Social Media',
  'image_resizer': 'Image Resizer',
  'image_generator': 'Lifestyle Image Generator',
  'ad_copy_generator': 'Ad Copy Generator',
  'product_listing_agent': 'AI Product Page Builder',
  'photo_studio': 'Photo Studio',
  'pricing': 'Pricing',
  'contact': 'Contact',
  'blog': 'Blog',
  'demos': 'Demos',
};

export function SEOHead({ pageId, noindex = false, defaultTitle, defaultDescription }: SEOHeadProps) {
  const [pageCopy, setPageCopy] = useState<PageCopy | null>(null);
  const [defaultSeoSettings, setDefaultSeoSettings] = useState<DefaultSeoSettings | null>(null);
  const [shouldNoindex, setShouldNoindex] = useState(noindex);
  const supabase = useSupabaseClient();

  const baseUrl = 'https://www.merchantfloor.com';
  const pagePath = PAGE_ID_TO_PATH[pageId] || '/';
  const canonicalUrl = `${baseUrl}${pagePath}`;

  useEffect(() => {
    async function checkLandingPageStatus() {
      try {
        const { data, error } = await supabase
          .from('landing_pages')
          .select('published')
          .eq('id', pageId)
          .maybeSingle();

        if (error) {
          console.error('Error checking landing page status:', error);
          setShouldNoindex(noindex);
          return;
        }

        if (data !== null) {
          setShouldNoindex(!data.published);
        } else {
          setShouldNoindex(noindex);
        }
      } catch {
        setShouldNoindex(noindex);
      }
    }

    checkLandingPageStatus();
  }, [supabase, pageId, noindex]);

  useEffect(() => {
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonicalUrl);

    let metaRobots = document.querySelector('meta[name="robots"]');
    if (!metaRobots) {
      metaRobots = document.createElement('meta');
      metaRobots.setAttribute('name', 'robots');
      document.head.appendChild(metaRobots);
    }
    metaRobots.setAttribute('content', shouldNoindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
  }, [canonicalUrl, shouldNoindex]);

  useEffect(() => {
    async function fetchPageCopy() {
      try {
        let data, error;

        if (pageId === 'pricing') {
          const response = await supabase
            .from('pricing_copy')
            .select('copy')
            .eq('id', 'pricing_seo')
            .maybeSingle();
          data = response.data;
          error = response.error;
        } else {
          const response = await supabase
            .from('page_copy')
            .select('copy')
            .eq('id', pageId)
            .maybeSingle();
          data = response.data;
          error = response.error;
        }

        if (error) {
          console.error('Error fetching page copy for SEO:', error);
          return;
        }

        if (data?.copy) {
          setPageCopy(data.copy);
        }
      } catch (err) {
        console.error('Error fetching page copy for SEO:', err);
      }
    }

    async function fetchDefaultSeoSettings() {
      try {
        const { data, error } = await supabase
          .from('page_copy')
          .select('copy')
          .eq('id', 'default_seo_settings')
          .maybeSingle();

        if (error) {
          console.error('Error fetching default SEO settings:', error);
          return;
        }

        if (data?.copy) {
          setDefaultSeoSettings(data.copy);
        }
      } catch (err) {
        console.error('Error fetching default SEO settings:', err);
      }
    }

    fetchPageCopy();
    fetchDefaultSeoSettings();
  }, [supabase, pageId]);

  const defaultShareImageUrl = `${baseUrl}/MF-Logo_1200x630.jpg`;
  const defaultShareImageAlt = 'MerchantFloor — AI launch assistant for ecommerce';

  useEffect(() => {
    if (shouldNoindex) return;

    const ensureMetaTag = (selector: string, attr: string, attrValue: string) => {
      let tag = document.querySelector(selector);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attr, attrValue);
        tag.setAttribute('data-seo-head', 'true');
        document.head.appendChild(tag);
      }
      return tag;
    };

    const ogUrl = ensureMetaTag('meta[property="og:url"]', 'property', 'og:url');
    ogUrl.setAttribute('content', canonicalUrl);

    const ogType = ensureMetaTag('meta[property="og:type"]', 'property', 'og:type');
    ogType.setAttribute('content', pageId === 'blog' ? 'blog' : 'website');

    const ogImage = ensureMetaTag('meta[property="og:image"]', 'property', 'og:image');
    ogImage.setAttribute('content', defaultShareImageUrl);

    const ogImageWidth = ensureMetaTag('meta[property="og:image:width"]', 'property', 'og:image:width');
    ogImageWidth.setAttribute('content', '1200');

    const ogImageHeight = ensureMetaTag('meta[property="og:image:height"]', 'property', 'og:image:height');
    ogImageHeight.setAttribute('content', '630');

    const ogImageAlt = ensureMetaTag('meta[property="og:image:alt"]', 'property', 'og:image:alt');
    ogImageAlt.setAttribute('content', defaultShareImageAlt);

    const twitterImage = ensureMetaTag('meta[name="twitter:image"]', 'name', 'twitter:image');
    twitterImage.setAttribute('content', defaultShareImageUrl);

    const twitterImageAlt = ensureMetaTag('meta[name="twitter:image:alt"]', 'name', 'twitter:image:alt');
    twitterImageAlt.setAttribute('content', defaultShareImageAlt);

    const twitterCard = ensureMetaTag('meta[name="twitter:card"]', 'name', 'twitter:card');
    twitterCard.setAttribute('content', 'summary_large_image');
  }, [shouldNoindex, defaultShareImageUrl, defaultShareImageAlt, canonicalUrl, pageId]);

  useEffect(() => {
    if (!pageCopy && !defaultSeoSettings && !defaultTitle) return;

    const title = pageCopy?.seoTitle ||
                  pageCopy?.title ||
                  defaultTitle ||
                  defaultSeoSettings?.defaultSeoTitle ||
                  'AI toolkit for online retailers - Try it free';
    document.title = withBrand(title);

    const description = pageCopy?.seoDescription ||
      (pageCopy?.subtitle ? stripHtmlTags(pageCopy.subtitle) : '') ||
      defaultDescription ||
      defaultSeoSettings?.defaultSeoDescription ||
      'From product photos to marketing content, our AI toolkit helps small businesses create & promote product listings 10x faster.';

    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      metaDescription.setAttribute('data-seo-head', 'true');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', description);

    const ensureMetaTag2 = (selector: string, attr: string, attrValue: string) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, attrValue);
        el.setAttribute('data-seo-head', 'true');
        document.head.appendChild(el);
      }
      return el;
    };

    const ogTitle = ensureMetaTag2('meta[property="og:title"]', 'property', 'og:title');
    ogTitle.setAttribute('content', title);

    const ogDesc = ensureMetaTag2('meta[property="og:description"]', 'property', 'og:description');
    ogDesc.setAttribute('content', description);
  }, [pageCopy, defaultSeoSettings, defaultTitle, defaultDescription]);

  useEffect(() => {
    if (shouldNoindex || (!pageCopy && !defaultSeoSettings && !defaultTitle)) return;

    const title = pageCopy?.seoTitle || pageCopy?.title || defaultTitle || defaultSeoSettings?.defaultSeoTitle || '';
    const description = pageCopy?.seoDescription ||
      (pageCopy?.subtitle ? stripHtmlTags(pageCopy.subtitle) : '') ||
      defaultDescription ||
      defaultSeoSettings?.defaultSeoDescription || '';

    document.querySelectorAll('script[data-seo-schema]').forEach(el => el.remove());

    const appendSchema = (data: object) => {
      const script = document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.setAttribute('data-seo-schema', 'true');
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
    };

    // Organization + WebSite schema on homepage
    if (pageId === 'home') {
      appendSchema({
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'MerchantFloor',
        url: baseUrl,
        logo: `${baseUrl}/MF-Logo_1200x630.jpg`,
        description: description || 'AI toolkit for online retailers',
        sameAs: [
          'https://www.instagram.com/merchantfloor',
          'https://apps.shopify.com/merchant-floor',
          'https://www.g2.com/products/merchant-floor/reviews',
        ],
      });

      appendSchema({
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'MerchantFloor',
        url: baseUrl,
        description: description || 'AI-powered product photos, descriptions, and marketing content for online stores',
        publisher: {
          '@type': 'Organization',
          name: 'MerchantFloor',
        },
      });
    }

    // SoftwareApplication schema on tool pages
    const appCategory = TOOL_APP_CATEGORY[pageId];
    if (appCategory && title) {
      appendSchema({
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: title,
        description: description || 'AI-powered tool for online retailers',
        url: canonicalUrl,
        applicationCategory: appCategory,
        operatingSystem: 'Web',
        publisher: {
          '@type': 'Organization',
          name: 'MerchantFloor',
          url: baseUrl,
        },
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'USD',
          description: 'Free tier available',
        },
      });
    }

    // BreadcrumbList schema on all indexed pages
    const breadcrumbLabel = PAGE_ID_TO_LABEL[pageId];
    if (breadcrumbLabel && pageId !== 'home') {
      appendSchema({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: baseUrl,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: breadcrumbLabel,
            item: canonicalUrl,
          },
        ],
      });
    }

    return () => {
      document.querySelectorAll('script[data-seo-schema]').forEach(el => el.remove());
    };
  }, [pageCopy, defaultSeoSettings, defaultTitle, defaultDescription, pageId, shouldNoindex, canonicalUrl, baseUrl]);

  return null;
}

// Helper function to strip HTML tags
function stripHtmlTags(html: string): string {
  if (!html) return '';
  
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  const textContent = tempDiv.textContent || tempDiv.innerText || '';
  return textContent.trim();
}